// The day chart, ported from the phone (ui/charts/GlucoseChart.kt, itself from nightscout-emailer's
// charts.ts): log-scale glucose, the 70–180 band, a low zone, dots coloured by range, pump carb
// and insulin bars rising from the bottom, and a "now" line. Above the plot, a lane of entries
// (nudged apart so they don't overlap) and a row of activity bars. Hovering shows what's under
// the pointer and clicking selects it. Times are drawn from epoch ms with the ticks the server
// computed, so the browser's time zone never matters.
//
// Everything inside is styled with SVG attributes and inline styles, not Tailwind classes, so
// the TV screen works on browsers too old for Tailwind's CSS.

import { scaleLinear, type ScaleLinear } from "d3-scale";
import { memo, useMemo, useState, type CSSProperties, type PointerEvent } from "react";
import { lastAtOrBefore, nearestIndex, placeMarkers } from "../../shared/chartLayout.js";
import type { EntryDTO, OtherNoteDTO, PumpDTO, WindowDTO } from "../../shared/dto.js";
import { GLUCOSE_TICKS, glucoseFraction, inRange, TARGET_HIGH, TARGET_LOW } from "../../shared/glucose.js";
import { roundHalfUp } from "../../shared/journal.js";
import { entryEmoji, headline, pumpSummary } from "../../shared/labels.js";
import { entryKey, noteKey, pumpKey } from "../../shared/timeline.js";
import { clockHourLabel, entryTimeText, formatTime, relativeHourLabel } from "@/lib/format";
import { useElementSize } from "@/lib/useElementSize";

/** Plot edges shared by charts stacked under this one, so their time axes line up. */
export const PLOT_LEFT = 38;
export const PLOT_RIGHT = 10;

/** Sizes in CSS pixels at scale 1. The TV screen scales them all up. */
const BASE = {
  left: PLOT_LEFT,
  right: PLOT_RIGHT,
  top: 52,
  bottom: 22,
  markerRadius: 11,
  noteRadius: 5,
  markerCenter: 13,
  markerRowBottom: 26,
  markerGap: 2,
  activityTop: 30,
  activityHeight: 16,
  activityMinWidth: 20,
  carbBarMax: 38,
  insulinBarMax: 22,
  font: 11,
  pumpFont: 10,
  emojiFont: 13,
  activityEmojiFont: 10,
  minTickSpacing: 54,
};

export function readingKey(t: number): string {
  return `reading:${t}`;
}

export interface GlucoseChartProps {
  data: WindowDTO;
  /** Fixed height in px; by default it follows the width. */
  height?: number;
  /** The current time, for the "now" line; omit on past days. */
  nowMs?: number;
  /** Multiplies every size, for the TV screen. */
  scale?: number;
  /** A timeline key (entry:…, pump:…, note:…) or a reading key. */
  selectedKey?: string | null;
  /** A time to point at, such as the timeline row under the pointer. */
  highlightT?: number | null;
  onSelect?: (key: string | null) => void;
  /** False on the TV screen: no hover or clicks. */
  interactive?: boolean;
}

export function GlucoseChart({ data, height, nowMs, scale = 1, selectedKey = null, highlightT = null, onSelect, interactive = true }: GlucoseChartProps) {
  const [ref, size] = useElementSize<HTMLDivElement>();
  const width = size.width;
  const h = height ?? autoHeight(width);
  const layout = useMemo(() => (width > 0 && h > 0 ? buildLayout(data, width, h, scale) : null), [data, width, h, scale]);
  const [hover, setHover] = useState<{ px: number; py: number; hit: Hit } | null>(null);

  function pointAt(e: PointerEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { px: e.clientX - rect.left, py: e.clientY - rect.top };
  }

  function onPointerMove(e: PointerEvent<SVGSVGElement>) {
    if (!layout) return;
    const { px, py } = pointAt(e);
    const hit = hitTest(layout, px, py);
    setHover(hit ? { px, py, hit } : null);
  }

  function onClick(e: React.MouseEvent<SVGSVGElement>) {
    if (!layout || !onSelect) return;
    const { px, py } = pointAt(e);
    const hit = hitTest(layout, px, py);
    const key = hit ? hitKey(hit) : null;
    onSelect(key !== null && key === selectedKey ? null : key);
  }

  const style: CSSProperties = { position: "relative", width: "100%", height: h, userSelect: "none", WebkitUserSelect: "none" };
  return (
    <div ref={ref} style={style}>
      {layout && (
        <svg
          width={layout.width}
          height={layout.height}
          role="img"
          aria-label="Glucose chart"
          style={{ display: "block", touchAction: "pan-y", cursor: interactive && hover ? "pointer" : undefined }}
          onPointerMove={interactive ? onPointerMove : undefined}
          onPointerDown={interactive ? onPointerMove : undefined}
          onPointerLeave={interactive ? (e) => e.pointerType === "mouse" && setHover(null) : undefined}
          onClick={interactive ? onClick : undefined}
        >
          <ChartBody layout={layout} nowMs={nowMs} />
          <Overlay layout={layout} hover={hover?.hit ?? null} selectedKey={selectedKey} highlightT={highlightT} />
        </svg>
      )}
      {layout && interactive && hover && <Tooltip layout={layout} {...hover} />}
    </div>
  );
}

function autoHeight(width: number) {
  return Math.round(Math.min(460, Math.max(300, width * 0.42)));
}

// ---- Layout -------------------------------------------------------------------------------

interface Plot {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

type Marker =
  | { kind: "entry"; key: string; t: number; trueX: number; x: number; radius: number; entry: EntryDTO }
  | { kind: "note"; key: string; t: number; trueX: number; x: number; radius: number; note: OtherNoteDTO };

interface ActivityBar {
  key: string;
  entry: Extract<EntryDTO, { kind: "activity" }>;
  /** The bar in the activity row, at least `activityMinWidth` wide. */
  x1: number;
  x2: number;
  /** The tint over the plot: the activity's real span. */
  spanX2: number;
}

interface PumpBar {
  key: string;
  pump: PumpDTO;
  x: number;
  carbH: number;
  insulinH: number;
  carbLabel: string | null;
  insulinLabel: string | null;
}

interface Layout {
  data: WindowDTO;
  k: number;
  width: number;
  height: number;
  plot: Plot;
  x: ScaleLinear<number, number>;
  y: (mgdl: number) => number;
  ticks: { t: number; x: number; labelX: number; label: string }[];
  markers: Marker[];
  activities: ActivityBar[];
  pump: PumpBar[];
  readingTimes: number[];
  dotRadius: number;
  /** The glucose ticks that get a label: all of them unless the plot is too short. */
  yLabels: Set<number>;
}

/** Most important first: on a short plot, labels are kept in this order while they don't collide. */
const LABEL_PRIORITY = [180, 55, 120, 260, 80, 400, 40];

function buildLayout(data: WindowDTO, width: number, height: number, k: number): Layout {
  const s = (v: number) => v * k;
  const plot: Plot = {
    left: s(BASE.left),
    right: Math.max(s(BASE.left) + 1, width - s(BASE.right)),
    top: s(BASE.top),
    bottom: Math.max(s(BASE.top) + 1, height - s(BASE.bottom)),
  };
  const x = scaleLinear().domain([data.startMs, data.endMs]).range([plot.left, plot.right]).clamp(true);
  const y = (mgdl: number) => plot.bottom - glucoseFraction(mgdl) * (plot.bottom - plot.top);

  // Label every hour if there's room, else every 2, 3, 4 or 6 hours, like the phone's 4.
  const hours = (data.endMs - data.startMs) / 3_600_000;
  const step = [1, 2, 3, 4, 6].find((st) => ((plot.right - plot.left) / hours) * st >= s(BASE.minTickSpacing)) ?? 6;
  const ticks = data.ticks
    .filter((t) => t.hour % step === 0)
    .map((t) => {
      const label = data.anchor !== null ? relativeHourLabel(t.hour) : clockHourLabel(t.hour);
      // Kept inside the chart, as on the phone: the last label would otherwise be cut off.
      const half = label.length * s(3.4);
      const tx = x(t.t);
      return { t: t.t, x: tx, label, labelX: Math.min(Math.max(tx, half), width - half) };
    });

  const unplaced = [
    ...data.entries.flatMap((entry) =>
      entry.kind === "activity" ? [] : [{ kind: "entry" as const, key: entryKey(entry), t: entry.t, trueX: x(entry.t), x: 0, radius: s(BASE.markerRadius), entry }],
    ),
    ...data.notes.map((note) => ({ kind: "note" as const, key: noteKey(note), t: note.t, trueX: x(note.t), x: 0, radius: s(BASE.noteRadius), note })),
  ].sort((a, b) => a.trueX - b.trueX);
  const xs = placeMarkers(unplaced, plot.left, plot.right, s(BASE.markerGap));
  const markers: Marker[] = unplaced.map((m, i) => ({ ...m, x: xs[i]! }));

  const activities = data.entries.flatMap((entry): ActivityBar[] => {
    if (entry.kind !== "activity") return [];
    const x1 = x(entry.t);
    const spanX2 = x(entry.endT);
    return [{ key: entryKey(entry), entry, x1, x2: Math.max(spanX2, x1 + s(BASE.activityMinWidth)), spanX2 }];
  });

  // Bar heights scale to the window's largest values, at least 60 g and 5 U, as on the phone.
  const carbMax = Math.max(60, ...data.pump.map((p) => p.carbs ?? 0));
  const insulinMax = Math.max(5, ...data.pump.map((p) => p.insulin ?? 0));
  const pump = data.pump.map((p) => ({
    key: pumpKey(p),
    pump: p,
    x: x(p.t),
    carbH: p.carbs ? (p.carbs / carbMax) * s(BASE.carbBarMax) : 0,
    insulinH: p.insulin ? Math.min((p.insulin / insulinMax) * s(BASE.insulinBarMax), s(BASE.insulinBarMax)) : 0,
    carbLabel: p.carbs !== null && p.carbs >= 15 ? `${Math.round(p.carbs)}g` : null,
    insulinLabel: p.insulin !== null && p.insulin >= 1 ? `${roundHalfUp(p.insulin, 1).toFixed(1)}U` : null,
  }));

  const yLabels = new Set<number>();
  for (const v of LABEL_PRIORITY) {
    if ([...yLabels].every((other) => Math.abs(y(v) - y(other)) >= s(BASE.font + 5))) yLabels.add(v);
  }

  return {
    data,
    k,
    width,
    height,
    plot,
    yLabels,
    x,
    y,
    ticks,
    markers,
    activities,
    pump,
    readingTimes: data.readings.map((r) => r[0]),
    // A few hours have far fewer dots than a day, so they can be bigger.
    dotRadius: (hours <= 8 ? 3.2 : 2.4) * k,
  };
}

// ---- Hit testing --------------------------------------------------------------------------

type Hit =
  | { kind: "marker"; marker: Marker }
  | { kind: "activity"; bar: ActivityBar }
  | { kind: "pump"; bar: PumpBar }
  | { kind: "reading"; t: number; mgdl: number };

function hitKey(hit: Hit): string {
  switch (hit.kind) {
    case "marker":
      return hit.marker.key;
    case "activity":
      return hit.bar.key;
    case "pump":
      return hit.bar.key;
    case "reading":
      return readingKey(hit.t);
  }
}

/** The phone's tap rules: the marker row, then the activity row, then pump bars, then readings. */
function hitTest(l: Layout, px: number, py: number): Hit | null {
  const s = (v: number) => v * l.k;

  if (py < s(BASE.markerRowBottom + 2)) {
    const i = nearestIndex(
      l.markers.map((m) => m.x),
      px,
      Infinity,
    );
    const m = l.markers[i];
    return m && Math.abs(m.x - px) <= m.radius + s(6) ? { kind: "marker", marker: m } : null;
  }

  if (py < l.plot.top) {
    let best: ActivityBar | null = null;
    let bestDistance = Infinity;
    for (const bar of l.activities) {
      const d = px < bar.x1 ? bar.x1 - px : px > bar.x2 ? px - bar.x2 : 0;
      if (d < bestDistance) {
        best = bar;
        bestDistance = d;
      }
    }
    return best && bestDistance <= s(6) ? { kind: "activity", bar: best } : null;
  }

  if (py > l.plot.bottom - s(40) && py <= l.plot.bottom) {
    const i = nearestIndex(
      l.pump.map((p) => p.x),
      px,
      s(12),
    );
    const bar = l.pump[i];
    if (bar) return { kind: "pump", bar };
  }

  if (py > l.plot.bottom || l.readingTimes.length === 0) return null;
  const t = l.x.invert(px);
  const before = lastAtOrBefore(l.readingTimes, t);
  const candidates = [before, before + 1].filter((i) => i >= 0 && i < l.readingTimes.length);
  const i = candidates[nearestIndex(
    candidates.map((c) => l.x(l.readingTimes[c]!)),
    px,
    s(20),
  )];
  if (i === undefined) return null;
  const [rt, mgdl] = l.data.readings[i]!;
  return { kind: "reading", t: rt, mgdl };
}

/** Where a key points on the chart, for the selection line. */
function locate(l: Layout, key: string): { x: number; marker?: Marker; reading?: { t: number; mgdl: number } } | null {
  if (key.startsWith("reading:")) {
    const t = Number(key.slice("reading:".length));
    const i = lastAtOrBefore(l.readingTimes, t);
    const r = l.data.readings[i];
    return r && r[0] === t ? { x: l.x(t), reading: { t, mgdl: r[1] } } : null;
  }
  const marker = l.markers.find((m) => m.key === key);
  if (marker) return { x: marker.trueX, marker };
  const activity = l.activities.find((a) => a.key === key);
  if (activity) return { x: activity.x1 };
  const pump = l.pump.find((p) => p.key === key);
  if (pump) return { x: pump.x };
  return null;
}

// ---- Drawing ------------------------------------------------------------------------------

const ChartBody = memo(function ChartBody({ layout: l, nowMs }: { layout: Layout; nowMs: number | undefined }) {
  const { plot, x, y, k } = l;
  const s = (v: number) => v * k;
  const plotW = plot.right - plot.left;
  const plotH = plot.bottom - plot.top;
  const dash = `${s(4)} ${s(4)}`;
  const label = { fontSize: s(BASE.font), fill: "var(--chart-label)" } as const;

  return (
    <g>
      <rect x={plot.left} y={plot.top} width={plotW} height={plotH} style={{ fill: "var(--chart-plot)" }} />
      <rect x={plot.left} y={y(TARGET_LOW)} width={plotW} height={plot.bottom - y(TARGET_LOW)} style={{ fill: "var(--chart-low)" }} />
      <rect x={plot.left} y={y(TARGET_HIGH)} width={plotW} height={y(TARGET_LOW) - y(TARGET_HIGH)} style={{ fill: "var(--chart-band)" }} />

      {/* Activities tint the plot for as long as they lasted. */}
      {l.activities.map((a) => (
        <rect key={a.key} x={a.x1} y={plot.top} width={Math.max(a.spanX2 - a.x1, 1)} height={plotH} style={{ fill: "var(--chart-activity-span)" }} />
      ))}

      {GLUCOSE_TICKS.map((v) => (
        <g key={v}>
          <line x1={plot.left} x2={plot.right} y1={y(v)} y2={y(v)} style={{ stroke: "var(--chart-grid)" }} shapeRendering="crispEdges" />
          {l.yLabels.has(v) && (
            <text x={plot.left - s(6)} y={y(v)} textAnchor="end" dominantBaseline="middle" style={label}>
              {v}
            </text>
          )}
        </g>
      ))}

      {l.ticks.map((t) => (
        <g key={t.t}>
          <line x1={t.x} x2={t.x} y1={plot.top} y2={plot.bottom} style={{ stroke: "var(--chart-grid)" }} shapeRendering="crispEdges" />
          <text x={t.labelX} y={plot.bottom + s(15)} textAnchor="middle" style={label}>
            {t.label}
          </text>
        </g>
      ))}

      {/* Dashed guides from each entry down through the plot, with a short connector when the
          marker had to be nudged sideways. */}
      {l.markers.map((m) =>
        m.kind === "entry" ? (
          <g key={m.key} style={{ stroke: "var(--chart-guide)" }} strokeWidth={1}>
            {Math.abs(m.x - m.trueX) > 1 && <line x1={m.x} y1={s(BASE.markerCenter) + m.radius} x2={m.trueX} y2={plot.top} />}
            <line x1={m.trueX} y1={plot.top} x2={m.trueX} y2={plot.bottom} strokeDasharray={dash} />
          </g>
        ) : null,
      )}

      {/* Pump carbs (red) and insulin (blue) from the bottom, as in the emailer. */}
      {l.pump.map((p) => {
        const top = plot.bottom - Math.max(p.carbH, p.insulinH) - s(3);
        const pumpLabel = { fontSize: s(BASE.pumpFont), fontWeight: 600 } as const;
        return (
          <g key={p.key}>
            {p.carbH > 0 && <rect x={p.x - s(4)} y={plot.bottom - p.carbH} width={s(3.5)} height={p.carbH} style={{ fill: "var(--chart-carbs)" }} />}
            {p.insulinH > 0 && <rect x={p.x + s(0.5)} y={plot.bottom - p.insulinH} width={s(2.5)} height={p.insulinH} style={{ fill: "var(--chart-insulin)" }} />}
            {p.insulinLabel && (
              <text x={p.x} y={top} textAnchor="middle" style={{ ...pumpLabel, fill: "var(--chart-insulin-label)" }}>
                {p.insulinLabel}
              </text>
            )}
            {p.carbLabel && (
              <text x={p.x} y={top - (p.insulinLabel ? s(11) : 0)} textAnchor="middle" style={{ ...pumpLabel, fill: "var(--chart-carbs-label)" }}>
                {p.carbLabel}
              </text>
            )}
          </g>
        );
      })}

      {l.data.readings.map(([t, mgdl]) => (
        <circle key={t} cx={x(t)} cy={y(mgdl)} r={l.dotRadius} style={{ fill: inRange(mgdl) ? "var(--chart-in-range)" : "var(--chart-out-of-range)" }} />
      ))}

      {/* The entry this window is about. */}
      {l.data.anchor !== null && (
        <line x1={x(l.data.anchor)} x2={x(l.data.anchor)} y1={plot.top} y2={plot.bottom} strokeWidth={s(1.5)} style={{ stroke: "var(--chart-anchor)" }} />
      )}

      {nowMs !== undefined && nowMs >= l.data.startMs && nowMs < l.data.endMs && (
        <line x1={x(nowMs)} x2={x(nowMs)} y1={plot.top} y2={plot.bottom} strokeWidth={s(1.5)} strokeDasharray={`${s(5)} ${s(4)}`} style={{ stroke: "var(--chart-now)" }} />
      )}

      <rect x={plot.left} y={plot.top} width={plotW} height={plotH} fill="none" style={{ stroke: "var(--chart-border)" }} shapeRendering="crispEdges" />

      {/* Activity row: one violet bar per activity, spanning its duration. */}
      {l.activities.map((a) => {
        const top = s(BASE.activityTop);
        const barH = s(BASE.activityHeight);
        return (
          <g key={a.key}>
            <rect x={a.x1} y={top} width={a.x2 - a.x1} height={barH} rx={barH / 2} style={{ fill: "var(--chart-activity)" }} opacity={0.85} />
            <text x={a.x1 + barH / 2 + s(1)} y={top + barH / 2} textAnchor="middle" dominantBaseline="central" style={{ fontSize: s(BASE.activityEmojiFont) }}>
              {entryEmoji(a.entry)}
            </text>
          </g>
        );
      })}

      {/* Marker row: grey dots for other sources' notes, emoji badges for entries. */}
      {l.markers.map((m) =>
        m.kind === "note" ? (
          <circle key={m.key} cx={m.x} cy={s(BASE.markerCenter)} r={m.radius} style={{ fill: "var(--chart-other-note)" }} />
        ) : (
          <g key={m.key}>
            <circle cx={m.x} cy={s(BASE.markerCenter)} r={m.radius} style={{ fill: "var(--chart-marker)" }} />
            <text x={m.x} y={s(BASE.markerCenter)} textAnchor="middle" dominantBaseline="central" style={{ fontSize: s(BASE.emojiFont) }}>
              {entryEmoji(m.entry)}
            </text>
          </g>
        ),
      )}
    </g>
  );
});

function Overlay({ layout: l, hover, selectedKey, highlightT }: { layout: Layout; hover: Hit | null; selectedKey: string | null; highlightT: number | null }) {
  const { plot, k } = l;
  const s = (v: number) => v * k;
  const selected = selectedKey ? locate(l, selectedKey) : null;
  const hovered = hover ? locate(l, hitKey(hover)) : null;

  return (
    <g pointerEvents="none">
      {highlightT !== null && highlightT >= l.data.startMs && highlightT < l.data.endMs && (
        <line x1={l.x(highlightT)} x2={l.x(highlightT)} y1={plot.top} y2={plot.bottom} strokeWidth={s(2)} style={{ stroke: "var(--chart-selection)" }} opacity={0.35} />
      )}
      {hovered && <Mark layout={l} at={hovered} opacity={0.45} />}
      {selected && <Mark layout={l} at={selected} opacity={1} />}
    </g>
  );
}

function Mark({ layout: l, at, opacity }: { layout: Layout; at: NonNullable<ReturnType<typeof locate>>; opacity: number }) {
  const s = (v: number) => v * l.k;
  return (
    <g opacity={opacity} style={{ stroke: "var(--chart-selection)" }} fill="none">
      <line x1={at.x} x2={at.x} y1={l.plot.top} y2={l.plot.bottom} strokeWidth={s(1)} />
      {at.reading && <circle cx={at.x} cy={l.y(at.reading.mgdl)} r={s(5)} strokeWidth={s(1.5)} />}
      {at.marker && <circle cx={at.marker.x} cy={s(BASE.markerCenter)} r={at.marker.radius + s(2)} strokeWidth={s(1.5)} />}
    </g>
  );
}

// ---- Tooltip ------------------------------------------------------------------------------

function Tooltip({ layout: l, px, py, hit }: { layout: Layout; px: number; py: number; hit: Hit }) {
  const zone = l.data.zone;
  const style: CSSProperties = { position: "absolute", pointerEvents: "none", zIndex: 10 };
  // Flip to the side and edge with more room.
  if (px > l.width / 2) style.right = l.width - px + 14;
  else style.left = px + 14;
  if (py > l.height * 0.6) style.bottom = l.height - py + 10;
  else style.top = py + 10;

  return (
    <div style={style} className="w-max max-w-64 rounded-xl border border-line bg-surface px-3 py-2 text-sm shadow-lg">
      <TooltipContent hit={hit} zone={zone} />
    </div>
  );
}

function TooltipContent({ hit, zone }: { hit: Hit; zone: string }) {
  switch (hit.kind) {
    case "reading":
      return (
        <>
          <div className="text-lg font-semibold tabular-nums" style={{ color: inRange(hit.mgdl) ? "var(--chart-in-range)" : "var(--chart-out-of-range)" }}>
            {hit.mgdl} <span className="text-xs font-normal text-muted">mg/dL</span>
          </div>
          <div className="text-xs text-muted">{formatTime(hit.t, zone)}</div>
        </>
      );
    case "pump":
      return (
        <>
          <div className="font-medium">{pumpSummary(hit.bar.pump)}</div>
          <div className="text-xs text-muted">{formatTime(hit.bar.pump.t, zone)} · pump</div>
        </>
      );
    case "activity":
      return <EntryTip entry={hit.bar.entry} zone={zone} />;
    case "marker":
      return hit.marker.kind === "entry" ? (
        <EntryTip entry={hit.marker.entry} zone={zone} />
      ) : (
        <>
          <div className="line-clamp-4">{hit.marker.note.text}</div>
          <div className="text-xs text-muted">
            {formatTime(hit.marker.note.t, zone)} · {hit.marker.note.enteredBy ?? "Nightscout"}
          </div>
        </>
      );
  }
}

function EntryTip({ entry, zone }: { entry: EntryDTO; zone: string }) {
  return (
    <>
      <div className="flex gap-2">
        <span aria-hidden>{entryEmoji(entry)}</span>
        <span className="line-clamp-3 font-medium">{headline(entry)}</span>
      </div>
      <div className="mt-0.5 text-xs text-muted">{entryTimeText(entry, zone)}</div>
    </>
  );
}
